import React, { useState, useEffect } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Loader2, Clock } from 'lucide-react';
import BusinessReservationsPage from './BusinessReservationsPage';
import BusinessMenuPage from './BusinessMenuPage';
import BusinessCardPage from './BusinessCardPage';
import StandaloneHeader from '../components/StandaloneHeader';
import CompanyFooter from '../components/CompanyFooter';
import { surfaceVars, isLightSurface } from '../data/motifs';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
axios.defaults.withCredentials = true;

export default function BusinessPublicPage() {
  const { userId, pageId } = useParams();
  const [searchParams] = useSearchParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Forward the arrival source (e.g. ?src=qr from a scanned QR code) so the
    // owner dashboard can attribute the visit.
    const src = searchParams.get('src');
    const base = pageId ? `${API}/business-page/${userId}/page/${pageId}` : `${API}/business-page/${userId}`;
    const url = src ? `${base}?src=${encodeURIComponent(src)}` : base;
    axios.get(url).then(({ data }) => setData(data)).catch(() => {}).finally(() => setLoading(false));
  }, [userId, pageId, searchParams]);

  if (loading) return <div className="min-h-screen flex items-center justify-center bg-[#0A0A0A]"><Loader2 className="w-8 h-8 animate-spin text-amber-500" /></div>;
  // A paused page with no template configured shows a simple message.
  if (data?.is_paused && (!data || !data.template)) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-[#0A0A0A] text-center px-6" data-testid="business-page-paused">
        <Clock className="w-12 h-12 text-amber-500 mb-4" />
        <h1 className="text-2xl font-semibold text-white mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>Īslaicīgi pauzēts</h1>
        <p className="text-white/50 max-w-sm">Šī biznesa lapa pašlaik ir pauzēta.</p>
      </div>
    );
  }
  if (!data || !data.template) {
    return <div className="min-h-screen flex items-center justify-center bg-[#0A0A0A] text-white/60" data-testid="business-page-empty">Šai lapai nav aktīva biznesa šablona.</div>;
  }

  // A paused page is still viewable — render the template with a paused banner on top.
  const dm = data.is_paused && data.pause_until ? (() => { const d = new Date(data.pause_until); return isNaN(d) ? '' : `${String(d.getDate()).padStart(2,'0')}.${String(d.getMonth()+1).padStart(2,'0')}`; })() : '';
  const banner = data.is_paused ? (
    <div className="sticky top-0 z-50 bg-amber-500 text-black text-sm font-semibold" data-testid="business-page-paused">
      <div className="max-w-5xl mx-auto px-4 py-2.5 flex items-center gap-2">
        <Clock className="w-4 h-4 flex-shrink-0" />
        <span data-testid="business-paused-title">{dm ? `Šī biznesa lapa ir pauzēta līdz ${dm} — rezervācijas pagaidām nav pieejamas` : 'Šī biznesa lapa īslaicīgi ir pauzēta — rezervācijas pagaidām nav pieejamas'}</span>
      </div>
    </div>
  ) : null;

  const Inner = data.template === 'menu' ? BusinessMenuPage : data.template === 'business_card' ? BusinessCardPage : BusinessReservationsPage;
  const owner = data.owner || {};
  const effPageId = data.id || pageId || null;
  const cfgKey = data.template === 'menu' ? 'menu' : data.template === 'business_card' ? 'business_card' : 'reservations';
  const surfaceId = (data[cfgKey] || {}).surface;
  const light = isLightSurface(surfaceId);
  return (
    <div style={{ ...surfaceVars(surfaceId), background: 'var(--bg-page)' }}>
      <StandaloneHeader theme={light ? 'light' : 'dark'} />
      {banner}
      <Inner data={data} userId={userId} pageId={effPageId} />
      <CompanyFooter
        theme={light ? 'light' : 'dark'}
        name={owner.name}
        logo={owner.logo}
        contacts={owner.contacts || {}}
        requisites={owner.requisites || {}}
      />
    </div>
  );
}
